import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { Set, Map, OrderedMap } from 'immutable';
import VisibilitySensor from 'react-visibility-sensor';

import User from 'models/user';
import { Location } from 'models/locations';

import { INPUT_FOCUS, INPUT_CLICK, INPUT_IMPRESSION } from 'lib/analytics/events';
import { routerLocationShape, dataCollectorShape, applePayInstanceShape } from 'lib/common/prop-types';

import SignInModal from 'containers/checkout/account-information/sign-in-modal';
import MarketingOptIn from 'containers/common/marketing-opt-in';

import RequiredField from 'components/common/required-field';
import ApplePayButton from 'components/common/buttons/apple-pay-button';

const propTypes = {
  user: PropTypes.instanceOf(User).isRequired,
  checkoutTotal: PropTypes.number,
  submitCheckout: PropTypes.func.isRequired,
  canSubmit: PropTypes.bool,
  setUserInfo: PropTypes.func.isRequired,
  modifyRequiredFieldList: PropTypes.func.isRequired,
  trackEvent: PropTypes.func.isRequired,
  routerLocation: routerLocationShape.isRequired,
  location: PropTypes.instanceOf(Location).isRequired,
  locale: PropTypes.string.isRequired,
  requiredFields: PropTypes.instanceOf(Set),
  requiredFieldErrors: PropTypes.oneOfType([
    PropTypes.instanceOf(Map),
    PropTypes.instanceOf(OrderedMap),
  ]),
  shouldScrollToError: PropTypes.bool,
  setShouldScrollToError: PropTypes.func.isRequired,
  email: PropTypes.string.isRequired,
  firstname: PropTypes.string.isRequired,
  lastname: PropTypes.string.isRequired,
  phone: PropTypes.string.isRequired,
  parent: PropTypes.instanceOf(Component).isRequired,
  onFirstnameFieldChange: PropTypes.func.isRequired,
  trackFirstName: PropTypes.func.isRequired,
  onLastnameFieldChange: PropTypes.func.isRequired,
  trackLastName: PropTypes.func.isRequired,
  onEmailFieldChange: PropTypes.func.isRequired,
  trackEmail: PropTypes.func.isRequired,
  onPhoneNumberFieldChange: PropTypes.func.isRequired,
  trackPhoneNumber: PropTypes.func.isRequired,
  formatPhoneNumber: PropTypes.func,
  onMarketingOptInChange: PropTypes.func.isRequired,
  applePayInstance: applePayInstanceShape,
  dataCollector: dataCollectorShape,
};

const defaultProps = {
  checkoutTotal: null,
  canSubmit: true,
  userInfo: Map(),
  requiredFields: Set(),
  requiredFieldErrors: Map(),
  shouldScrollToError: false,
  dataCollector: null,
  applePayInstance: null,
  isReduceDistractionsABTest: true,
  formatPhoneNumber: () => {},
};

const GuestAccountInformation = ({
  user,
  location,
  locale,
  email,
  firstname,
  lastname,
  phone,
  submitCheckout,
  checkoutTotal,
  applePayInstance,
  dataCollector,
  canSubmit,
  setUserInfo,
  modifyRequiredFieldList,
  requiredFieldErrors,
  requiredFields,
  shouldScrollToError,
  setShouldScrollToError,
  trackEvent,
  routerLocation,
  parent,
  onFirstnameFieldChange,
  trackFirstName,
  onLastnameFieldChange,
  trackLastName,
  onEmailFieldChange,
  trackEmail,
  onPhoneNumberFieldChange,
  trackPhoneNumber,
  formatPhoneNumber,
  onMarketingOptInChange,
}) => {
  if (user.isLoggedIn()) { return null; }

  return (
    <div className="border-color-light-grey border-side-top border-style-solid padding-top-20">
      <div className="row">
        <div className="col-xs-12">
          <h2 className="text-color-dark-slate text-size-20 text-weight-bold margin-0">Your Information</h2>
        </div>
      </div>
      <div className="row">
        <div className="col-md-6 col-xs-12">
          <ApplePayButton
            submitCheckout={submitCheckout}
            checkoutTotal={checkoutTotal}
            applePayInstance={applePayInstance}
            dataCollector={dataCollector}
            orLabel="after"
            className="checkout-margin-left-md text-center"
            canSubmit={canSubmit}
            setUserInfo={setUserInfo}
            modifyRequiredFieldList={modifyRequiredFieldList}
            trackEvent={trackEvent}
            location={location}
            locale={locale}
            position="1"
            display
          />
        </div>
      </div>
      <div className="row text-size-12 text-weight-book margin-bottom-10">
        <div className="col-xs-12 text-align-right">
          <SignInModal routerLocation={routerLocation} />
        </div>
      </div>
      <div>
        <div id="reserve-login-fields">
          <div className="row">
            <div className="col-xs-6 form-group">
              <label htmlFor="firstname" className="required-field">
                <FormattedMessage
                  id="checkout.first-name"
                  defaultMessage="FIRST NAME"
                />
              </label>
              <VisibilitySensor onChange={(v) => { trackFirstName(INPUT_IMPRESSION, v); }} partialVisibility>
                <RequiredField
                  key="firstname"
                  modifyRequiredFieldList={modifyRequiredFieldList}
                  requiredFieldErrors={requiredFieldErrors}
                  requiredFields={requiredFields}
                  shouldScrollToError={shouldScrollToError}
                  setShouldScrollToError={setShouldScrollToError}
                >
                  <input
                    id="firstname"
                    name="firstname"
                    data-field-name="firstname"
                    className="form-control"
                    placeholder="First Name"
                    value={firstname}
                    type="text"
                    onChange={onFirstnameFieldChange}
                    onFocus={() => { trackFirstName(INPUT_FOCUS); }}
                    onClick={() => { trackFirstName(INPUT_CLICK); }}
                    // eslint-disable-next-line no-param-reassign
                    ref={(firstnameField) => { parent.firstnameField = firstnameField; }}
                  />
                </RequiredField>
              </VisibilitySensor>
            </div>
            <div className="col-xs-6 form-group">
              <label htmlFor="lastname" className="required-field">
                <FormattedMessage
                  id="checkout.last-name"
                  defaultMessage="LAST NAME"
                />
              </label>
              <VisibilitySensor onChange={(v) => { trackLastName(INPUT_IMPRESSION, v); }} partialVisibility>
                <RequiredField
                  key="lastname"
                  modifyRequiredFieldList={modifyRequiredFieldList}
                  requiredFieldErrors={requiredFieldErrors}
                  requiredFields={requiredFields}
                  shouldScrollToError={shouldScrollToError}
                  setShouldScrollToError={setShouldScrollToError}
                >
                  <input
                    id="lastname"
                    name="lastname"
                    data-field-name="lastname"
                    className="form-control"
                    placeholder="Last Name"
                    value={lastname}
                    type="text"
                    onChange={onLastnameFieldChange}
                    onFocus={() => { trackLastName(INPUT_FOCUS); }}
                    onClick={() => { trackLastName(INPUT_CLICK); }}
                    // eslint-disable-next-line no-param-reassign
                    ref={(lastnameField) => { parent.lastnameField = lastnameField; }}
                  />
                </RequiredField>
              </VisibilitySensor>
            </div>
          </div>
          <div className="row">
            <div className="col-xs-12">
              <div className="form-group">
                <label htmlFor="email" className="required-field">EMAIL</label>
                <VisibilitySensor onChange={(v) => { trackEmail(INPUT_IMPRESSION, v); }} partialVisibility>
                  <RequiredField
                    key="email"
                    modifyRequiredFieldList={modifyRequiredFieldList}
                    requiredFieldErrors={requiredFieldErrors}
                    requiredFields={requiredFields}
                    shouldScrollToError={shouldScrollToError}
                    setShouldScrollToError={setShouldScrollToError}
                  >
                    <input
                      id="email"
                      name="email"
                      data-field-name="email"
                      className="form-control"
                      placeholder="name@email.com"
                      value={email}
                      onChange={onEmailFieldChange}
                      onFocus={() => { trackEmail(INPUT_FOCUS); }}
                      onClick={() => { trackEmail(INPUT_CLICK); }}
                      // eslint-disable-next-line no-param-reassign
                      ref={(emailField) => { parent.emailField = emailField; }}
                    />
                  </RequiredField>
                </VisibilitySensor>
              </div>
            </div>
            <MarketingOptIn className="col-xs-12 margin-bottom-30" onChange={onMarketingOptInChange} />
            <div className="col-xs-12">
              <div className="form-group">
                <label htmlFor="phone">
                  <FormattedMessage
                    id="checkout.phone-number"
                    defaultMessage="PHONE NUMBER"
                  />
                </label>
                <VisibilitySensor onChange={(v) => { trackPhoneNumber(INPUT_IMPRESSION, v); }} partialVisibility>
                  <input
                    type="tel"
                    id="phoneNumber"
                    name="phoneNumber"
                    data-field-name="phone"
                    className="form-control"
                    placeholder="(__) __-___"
                    value={formatPhoneNumber(phone)}
                    onChange={onPhoneNumberFieldChange}
                    onFocus={() => { trackPhoneNumber(INPUT_FOCUS); }}
                    onClick={() => { trackPhoneNumber(INPUT_CLICK); }}
                    // eslint-disable-next-line no-param-reassign
                    ref={(phoneField) => { parent.phoneField = phoneField; }}
                  />
                </VisibilitySensor>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

GuestAccountInformation.propTypes = propTypes;
GuestAccountInformation.defaultProps = defaultProps;
export default GuestAccountInformation;

